<template>
    <div class="d-flex align-items-center">
        <div class="flex-grow-1 pl-3 py-2">
            <AppIcon v-if="icon.name" :name="icon.name" :color="icon.color" class="pr-2"/>
            {{ subtype.display }}
        </div>

        <div v-if="subtype.isDefaultCreate"
             v-tooltip="'Default for creation'"
             class="px-1 text-success">
            <i class="fal fa-file-circle-plus"></i>
        </div>
        <div v-if="subtype.isDefaultPipeline"
             v-tooltip="'Default for pipeline'"
             class="px-1 text-warning">
            <i class="fal fa-timeline-arrow"></i>
        </div>
        <div class="py-1 px-2">
            <v-popover :open.sync="dropdown" popover-inner-class="p-1">
                <div @click.stop="dropdown = !dropdown" class="list-group-item-btn">
                    <i class="fas fa-sm fa-ellipsis-h"></i>
                </div>
                <template v-slot:popover>
                    <div v-close-popover>
                        <div @click="onEdit" class="dropdown-item">
                            <span class="pr-2 text-primary-light">
                                <i class="fal fa-pencil"></i>
                            </span>
                            Edit form
                        </div>
                        <div @click="onIconSet" class="dropdown-item">
                            <span class="pr-2 text-primary-light">
                                <i class="fal fa-symbols"></i>
                            </span>
                            Set icon
                        </div>
                        <div @click="onDuplicate" class="dropdown-item">
                            <span class="pr-2">
                                <i class="fal fa-copy"></i>
                            </span>
                            Duplicate
                        </div>
                        <div @click="onSetIsDefaultCreate" class="dropdown-item">
                            <span class="pr-2 text-success">
                                <i class="fal fa-file-circle-plus"></i>
                            </span>
                            {{ subtype.isDefaultCreate ? 'Unset' : 'Set' }} as default for Creation
                        </div>
                        <div @click="onToggleDefaultPipeline" class="dropdown-item">
                            <span class="pr-2 text-warning">
                                <i class="fal fa-timeline-arrow"></i>
                            </span>
                            {{ subtype.isDefaultPipeline ? 'Unset' : 'Set' }} as default for Pipeline
                        </div>
                        <div @click="onDelete" class="dropdown-item">
                            <span class="pr-2 text-danger">
                                <i class="fal fa-trash"></i>
                            </span>
                            Delete
                        </div>
                    </div>
                </template>
            </v-popover>
        </div>
    </div>
</template>

<script>
import {withConfirmation} from '@/decorators/withConfirmation'
import {showErrorMessage} from '@/services/snackbar'
import {documentTypesApiService} from '@/api/document/types'
import {VersionedName} from '@/utils/string'
import AppIcon from '@/components/shared/AppIcon'

export default {
    name: 'DocSubtypeSettingsListItem',
    components: {AppIcon},
    props: {
        subtype: {
            type: Object
        },
        subtypes: {
            type: Array
        },
        isLoading: {
            type: Boolean
        },
        isSelected: {
            type: Boolean
        },
        isSaving: {
            type: Boolean
        }
    },
    data() {
        return {
            isDeleting: false,
            isHovered: false,
            dropdown: false
        }
    },
    computed: {
        isSystem() {
            return this.subtype.type === 'SYSTEM'
        },

        subtypeKey() {
            return this.subtype.section
        },

        icon() {
            return _.get(this.subtype, 'icon', {}) || {}
        }
    },
    methods: {
        async onDuplicate() {
            const display = this.findFreeNameForDuplicatedSubtypeProperty('display', ' Copy ')
            const property = this.findFreeNameForDuplicatedSubtypeProperty('property', '_COPY')
            const subtype = {...this.subtype, uuid: null, display, property}
            await documentTypesApiService.writeSubtype(this.subtypeKey, subtype)
            this.$emit('refresh')
        },

        findFreeNameForDuplicatedSubtypeProperty(prop, versionPrefix) {
            const find = (name, versionPrefix) => {
                const nextName = this.getNextVersionName(name, versionPrefix)
                if (this.checkIsNameExists(prop, nextName)) {
                    return find(nextName, versionPrefix)
                }
                return nextName
            }

            const currentName = _.get(this.subtype, prop, '')
            return find(currentName, versionPrefix)
        },

        checkIsNameExists(prop, name) {
            return this.subtypes.some(s => _.get(s, prop) === name)
        },

        getNextVersionName(name, versionPrefix = ' Copy ') {
            const versionedName = new VersionedName({name, versionPrefix})
            versionedName.increment()
            return versionedName.name
        },

        onEdit() {
            this.$emit('edit')
        },

        onDelete() {
            withConfirmation({
                title: 'Delete document subtype',
                description: 'Are you sure you want to delete this document subtype?',
                onConfirm: this.deleteSubtype
            })
        },

        async deleteSubtype() {
            this.isDeleting = true
            try {
                if (!this.isSystem) {
                    await documentTypesApiService.writeSubtype(this.subtypeKey, {
                        ...this.subtype,
                        isHidden: true
                    })
                } else {
                    await documentTypesApiService.removeSubtype(this.subtypeKey, this.subtype.uuid)
                }
                this.$emit('deleted')
            } catch (e) {
                showErrorMessage('Failed to delete subtype')
            } finally {
                this.isDeleting = false
            }
        },

        async onSetIsDefaultCreate() {
            await documentTypesApiService.writeSubtype(this.subtypeKey, {
                ...this.subtype,
                isDefaultCreate: !this.subtype.isDefaultCreate
            })
            this.$emit('refresh')
        },

        async onToggleDefaultPipeline() {
            await documentTypesApiService.writeSubtype(this.subtypeKey, {
                ...this.subtype,
                isDefaultPipeline: !this.subtype.isDefaultPipeline
            })
            this.$emit('refresh')
        },

        onIconSet() {
            this.$modal.show('edit-doc-subtype-icon', {
                icon: this.icon,
                onSave: this.onIconSave
            })
        },

        async onIconSave(icon) {
            await documentTypesApiService.writeSubtype(this.subtypeKey, {...this.subtype, icon})
            this.$emit('refresh')
        },

        hideDropdown() {
            this.dropdown = false
        }
    }
}
</script>

<style scoped lang="scss">
.delete-btn {
    color: $color-danger;
}
</style>
